export interface Project {
    id: string;
    name: string;
    description: string;
    status: string;
    created_at: string;
    updated_at: string;
    user_id: string;
  }

// Base interfaces for reusability
export interface MachineConfig {
  enabled: boolean;
  category?: string;
  feature?: string;
  machine_id?: number;
  nonCRCMachines?: {
    enabled: boolean;
    machines: any;
  };
}

export interface FeatureConfig {
  enabled: boolean;
  machines: any;
}

export interface WeldingConfig extends FeatureConfig {
  machines: {
    IWM?: MachineConfig;
    P625?: MachineConfig;
    P600Z?: MachineConfig;
    M500?: MachineConfig;
    Manual?: MachineConfig;
    competitorMachine?: MachineConfig;
  };
}

export interface FeaturesConfig {
  materialTraceability: FeatureConfig;
  pipeFitter: FeatureConfig;
  welding: WeldingConfig;
  ndt: FeatureConfig;
  /** Fabrication-only; gates Hydro column on assembly table */
  hydro?: FeatureConfig;
  /** Fabrication-only; gates Torquing column on assembly table */
  torquing?: FeatureConfig;
  coating: FeatureConfig;
}

export interface FabricationOption {
  selected: boolean;
  features: FeaturesConfig;
  project_type_id?: number;
  isDocumentAvailable?: boolean;
  isPipeAvailable?: boolean;
}

export interface ProjectTypeConfig {
  enabled: boolean;
  project_type_id?: number;
  features: FeaturesConfig;
  isDocumentAvailable?: boolean;
  isPipeAvailable?: boolean;
  options: {
    buckleArrestor: FabricationOption;
    pipeInPipe: FabricationOption;
    bulkhead: FabricationOption;
    spools: FabricationOption;
  };
}

export interface ProjectDataAPI {
  projectName?: string;
  projectNumber?: string;
  location?: string;
  customer?: string;
  contractorName?: string;
  unit?: string;
  startDate?: string;
  endDate?: string;
  progress?: string;
  projectTypes: {
    pipeline: ProjectTypeConfig;
    fabrication: ProjectTypeConfig;
    others: ProjectTypeConfig;
  };
}

// Helper functions for creating default configurations
export const createDefaultMachineConfig = (category: string, feature: string): MachineConfig => ({
  enabled: false,
  category,
  feature,
});

export const createDefaultWeldingConfig = (): WeldingConfig => ({
  enabled: false,
  machines: {
    IWM: createDefaultMachineConfig("CRCE", "WELDING"),
    P625: createDefaultMachineConfig("CRCE", "WELDING"),
    P600Z: createDefaultMachineConfig("CRCE", "WELDING"),
    M500: createDefaultMachineConfig("CRCE", "WELDING"),
    Manual: {
      ...createDefaultMachineConfig("NonCRCE", "WELDING"),
      nonCRCMachines: { enabled: false, machines: {} }
    },
    competitorMachine: {
      ...createDefaultMachineConfig("NonCRCE", "WELDING"),
      nonCRCMachines: { enabled: false, machines: {} }
    },
  },
});

export const createDefaultFeaturesConfig = (): FeaturesConfig => ({
  materialTraceability: { enabled: false, machines: {} },
  pipeFitter: { enabled: false, machines: {} },
  welding: createDefaultWeldingConfig(),
  ndt: { enabled: false, machines: {} },
  hydro: { enabled: false, machines: {} },
  torquing: { enabled: false, machines: {} },
  coating: {
    enabled: false,
    machines: {
      pumpHouse: createDefaultMachineConfig('pumpHouse', 'Coating'),
    },
  },
});

/** Merge pipeline coating from API; supports legacy `coating.pumpHouse` and `coating.machines.pumpHouse`. */
export const mergePipelineCoatingFromApi = (
  defaults: FeaturesConfig['coating'],
  api: FeaturesConfig['coating'] | undefined
): FeatureConfig => {
  const raw = api as (FeaturesConfig['coating'] & { pumpHouse?: { enabled?: boolean } }) | undefined;
  const source = raw ?? defaults;
  const { pumpHouse: legacyPumpHouse, ...rest } = source as FeaturesConfig['coating'] & {
    pumpHouse?: { enabled?: boolean };
  };
  const defPump = defaults.machines?.pumpHouse ?? createDefaultMachineConfig('pumpHouse', 'Coating');
  return {
    ...defaults,
    ...rest,
    machines: {
      ...defaults.machines,
      ...rest.machines,
      pumpHouse: {
        ...createDefaultMachineConfig('pumpHouse', 'Coating'),
        ...defPump,
        ...rest.machines?.pumpHouse,
        enabled: !!(
          rest.machines?.pumpHouse?.enabled ??
          legacyPumpHouse?.enabled ??
          defPump.enabled
        ),
      },
    },
  };
};

export const createDefaultFabricationOption = (): FabricationOption => ({
  selected: false,
  features: createDefaultFeaturesConfig(),
  isDocumentAvailable: false,
  isPipeAvailable: false,
});

export const createDefaultProjectTypeConfig = (): ProjectTypeConfig => ({
  enabled: false,
  features: createDefaultFeaturesConfig(),
  options: {
    buckleArrestor: createDefaultFabricationOption(),
    pipeInPipe: createDefaultFabricationOption(),
    bulkhead: createDefaultFabricationOption(),
    spools: createDefaultFabricationOption(),
  },
});

export const createDefaultFormData = (): ProjectDataAPI => ({
  projectName: '',
  projectNumber: '',
  location: '',
  customer: '',
  contractorName: '',
  unit: 'in',
  startDate: '',
  endDate: '',
  projectTypes: {
    pipeline: createDefaultProjectTypeConfig(),
    fabrication: createDefaultProjectTypeConfig(),
    others: createDefaultProjectTypeConfig(),
  },
});